import { createContext, useContext, useMemo, useState } from "react"

import type { BusinessInfo } from "@/shared/api/generated/types"

import { useUserBusinesses } from "./use-user-businesses"

type SelectedBusinessContextValue = {
	businesses: BusinessInfo[]
	selectedBusinessId: string
	selectedBusinessName: string
	setSelectedBusinessId: (id: string) => void
	isLoading: boolean
}

const SelectedBusinessContext =
	createContext<SelectedBusinessContextValue | null>(null)

export function useSelectedBusiness() {
	const context = useContext(SelectedBusinessContext)
	if (!context) {
		throw new Error(
			"useSelectedBusiness must be used within a SelectedBusinessProvider",
		)
	}
	return context
}

export function SelectedBusinessProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const { data: businessesData, isLoading } = useUserBusinesses()
	const businesses = businessesData?.businesses ?? []

	const [selectedBusinessId, setSelectedBusinessId] = useState<string>("")

	const value = useMemo<SelectedBusinessContextValue>(() => {
		const resolvedId =
			selectedBusinessId &&
			businesses.some((b) => b.internalID === selectedBusinessId)
				? selectedBusinessId
				: (businesses[0]?.internalID ?? "")

		const selectedBusinessName =
			businesses.find((b) => b.internalID === resolvedId)?.name ?? ""

		return {
			businesses,
			selectedBusinessId: resolvedId,
			selectedBusinessName,
			setSelectedBusinessId,
			isLoading,
		}
	}, [businesses, selectedBusinessId, isLoading])

	return (
		<SelectedBusinessContext.Provider value={value}>
			{children}
		</SelectedBusinessContext.Provider>
	)
}
